import { useSharedCalendarState } from "@/components/canvas/useSharedCalendarState.ts";
import { getDateRange } from "@/components/calendars/dateRange.ts";
import type { ViewType } from "@/components/calendars/types.ts";
import { isWithinInterval } from "date-fns";
import { atom, getDefaultStore, useAtomValue, useSetAtom } from "jotai";
import { type Dispatch, type SetStateAction, useCallback } from "react";

/**
 * A bump-counter that requests the calendar grid scroll to the current time.
 * Shared (module-level) so the header's Today button, the page's `t` shortcut,
 * and the grid that consumes it all pulse through one value even though they
 * mount as separate hook instances on separate surfaces.
 */
const scrollToNowSignalAtom = atom(0);

/**
 * Reads the scroll-to-now pulse. Split out from {@link useCanvasCalendarNav} so
 * only the grid subscribes to the counter — the header, day strip, surface, and
 * page fire the pulse via `scrollToNow`/`goToToday` but never read it, so they
 * no longer re-render when it bumps.
 */
export const useScrollToNowKey = (): number => useAtomValue(scrollToNowSignalAtom);

/**
 * A request to scroll the grid to a specific wall-clock time ("HH:mm"), carrying
 * a sequence number so the same time requested twice still scrolls — a user who
 * scrolled away and re-clicked the link expects to land back on it.
 *
 * Separate from the scroll-to-now pulse above because it names a target rather
 * than meaning "now", and transient (never persisted) unlike the view/date: it
 * is a one-shot instruction to a mounted grid, not calendar state.
 */
const scrollToTimeAtom = atom<{ time: string; seq: number } | null>(null);

/** Reads the pending scroll-to-time request. Only the grid subscribes. */
export const useScrollToTimeRequest = () => useAtomValue(scrollToTimeAtom);

/**
 * Ask whichever calendar grid is mounted to scroll to a wall-clock time.
 * Imperative and hook-free so the calendar focus applier can call it while
 * applying a link, without taking a subscription that would re-render the
 * boundary's whole subtree.
 */
export function requestCalendarScrollToTime(time: string): void {
  const store = getDefaultStore();
  const prev = store.get(scrollToTimeAtom);
  store.set(scrollToTimeAtom, { time, seq: (prev?.seq ?? 0) + 1 });
}

/** Test-only: drop any pending scroll request between cases. */
export function __resetScrollToTime(): void {
  getDefaultStore().set(scrollToTimeAtom, null);
}

/**
 * Resolves the anchor date for a user-driven view switch: today when it falls
 * inside the new view's window (so switching opens on the current day), else
 * `null` to leave the date put. Multi-day spans two days, so it checks the real
 * visible window — otherwise switching to multi-day with `currentDate + 1` ===
 * today would miss the snap and anchor the view on yesterday.
 */
const resolveViewSwitchSnapDate = (
  currentDate: Date,
  newViewType: ViewType,
  today: Date,
): Date | null => {
  const { start, end } = getDateRange(
    currentDate,
    newViewType,
    newViewType === "multi-day" ? 2 : 1,
  );
  return isWithinInterval(today, { start, end }) ? today : null;
};

/**
 * The one calendar-navigation source of truth for every canvas surface (home,
 * the thread canvas, and the shared header). Wraps the durable
 * {@link useSharedCalendarState} with the two behaviors that must be identical
 * across surfaces so switching between them never behaves differently:
 *
 * - `setViewType` snaps the date to today when today falls inside the new view's
 *   window (so switching view opens on the current day).
 * - `scrollToNow` / `goToToday` drive a shared scroll-to-now pulse.
 */
export const useCanvasCalendarNav = (): {
  currentDate: Date;
  setCurrentDate: Dispatch<SetStateAction<Date>>;
  viewType: ViewType;
  /** Snap-to-today-aware view setter for user-driven switches (header, keyboard). */
  setViewType: (viewType: ViewType) => void;
  /**
   * The un-snapped view setter. Use for programmatic navigation that sets an
   * explicit date + view together (e.g. a `/calendar?date=&view=` link), where
   * snapping to today would clobber the linked date.
   */
  setViewTypeRaw: (viewType: ViewType) => void;
  /** Pulse scroll-to-now without moving the date (the header's Today button). */
  scrollToNow: () => void;
  /** Jump to today and scroll to now (the `t` shortcut). */
  goToToday: () => void;
} => {
  const {
    currentDate,
    setCurrentDate,
    viewType,
    setViewType: setStoredView,
  } = useSharedCalendarState();
  const setScrollToNowSignal = useSetAtom(scrollToNowSignalAtom);

  const scrollToNow = useCallback(() => setScrollToNowSignal((k) => k + 1), [setScrollToNowSignal]);

  const goToToday = useCallback(() => {
    setCurrentDate(new Date());
    scrollToNow();
  }, [setCurrentDate, scrollToNow]);

  const setViewType = useCallback(
    (newViewType: ViewType) => {
      const snapDate = resolveViewSwitchSnapDate(currentDate, newViewType, new Date());
      if (snapDate) setCurrentDate(snapDate);
      setStoredView(newViewType);
    },
    [currentDate, setStoredView, setCurrentDate],
  );

  return {
    currentDate,
    setCurrentDate,
    viewType,
    setViewType,
    setViewTypeRaw: setStoredView,
    scrollToNow,
    goToToday,
  };
};
